/**
 * Precision CAP Portal — invitation redemption.
 *
 * The vendor (FDR contact) clicks the link in their invitation email. The
 * URL path carries the one-time token (/redeem/:token); they fill in their
 * full name and pick a password. After redeem succeeds the backend returns
 * an enroll_token and we send them straight into MFA enrollment.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_API_URL || '';

function passwordIssues(pw) {
  const issues = [];
  if (pw.length < 12) issues.push('at least 12 characters');
  if (!/[A-Z]/.test(pw)) issues.push('an uppercase letter');
  if (!/[a-z]/.test(pw)) issues.push('a lowercase letter');
  if (!/[0-9]/.test(pw)) issues.push('a number');
  if (!/[^A-Za-z0-9]/.test(pw)) issues.push('a symbol');
  return issues;
}

export default function PortalRedeem() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const issues = useMemo(() => passwordIssues(password), [password]);
  const passwordOk = password.length > 0 && issues.length === 0;
  const matches = password.length > 0 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordOk) {
      setError(`Your password still needs ${issues.join(', ')}.`);
      return;
    }
    if (!matches) {
      setError('The two passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password,
          full_name: fullName.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || 'We could not redeem this invitation. The link may have expired.');
        setLoading(false);
        return;
      }

      // Match whatever the backend hands back. The spec says redeem returns
      // {external_user_id, mfa_required:true}; if it also returns an
      // enroll_token, jump straight into enrollment. Otherwise send them to
      // /login so they can sign in with their new password and the server
      // will issue an enroll_token at that step.
      if (data.enroll_token) {
        sessionStorage.setItem('portal_enroll_token', data.enroll_token);
        navigate('/mfa/enroll', { state: { enroll_token: data.enroll_token } });
        return;
      }
      navigate('/login');
    } catch (err) {
      setError('We could not reach the server. Please try again.');
      setLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-md mx-auto">
        <h1 className="text-[28px] font-semibold text-slate-900 leading-tight">
          Welcome to Precision CAP Portal
        </h1>
        <p className="text-slate-600 mt-2 text-[15px]">
          A few details and you'll have access to the corrective action plan we're working on
          together. This takes about a minute.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 bg-white ring-1 ring-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <label htmlFor="redeem-name" className="block text-sm font-medium text-slate-700 mb-1">
              Your full name
            </label>
            <input
              id="redeem-name"
              type="text"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="redeem-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="redeem-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            <p className="text-[12px] text-slate-500 mt-1">
              Use the email this invitation was sent to.
            </p>
          </div>

          <div>
            <label htmlFor="redeem-pw" className="block text-sm font-medium text-slate-700 mb-1">
              Choose a password
            </label>
            <input
              id="redeem-pw"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            {password.length > 0 && (
              <p className={`text-[12px] mt-1 ${passwordOk ? 'text-emerald-700' : 'text-slate-500'}`}>
                {passwordOk
                  ? 'Looks good.'
                  : `Still need ${issues.join(', ')}.`}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="redeem-confirm" className="block text-sm font-medium text-slate-700 mb-1">
              Confirm password
            </label>
            <input
              id="redeem-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            {confirm.length > 0 && !matches && (
              <p className="text-[12px] mt-1 text-red-700">
                The two passwords do not match.
              </p>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !fullName || !email || !passwordOk || !matches}
            className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition ${
              loading || !fullName || !email || !passwordOk || !matches
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {loading ? 'Setting up your account…' : 'Continue'}
          </button>

          <p className="text-[12px] text-slate-500">
            Next we'll have you turn on two-factor authentication. It takes about two minutes
            and uses any standard authenticator app on your phone.
          </p>
        </form>
      </div>
    </PortalLayout>
  );
}
